import { RefObject, useEffect } from 'react';
import { getDOMElements, getInitialViewportHeight } from '../utils/domElements';
import { isIOS } from '../utils/platformDetection';

/**
 * Locks body scroll on mobile devices to prevent background scrolling.
 * Manages html, body, and root container styles with proper cleanup on unmount.
 */
interface UseBodyScrollLockOptions {
  enabled: boolean;
  containerRef?: RefObject<HTMLElement>;
}

export const useBodyScrollLock = ({
  enabled,
  containerRef,
}: UseBodyScrollLockOptions) => {
  useEffect(() => {
    if (!enabled) return;

    const { html, body, root, rootContainer } = getDOMElements();
    const lockContainer = containerRef?.current ?? rootContainer;
    const scrollY = window.scrollY;

    const originalHtmlOverflow = html.style.overflow;
    const originalHtmlHeight = html.style.height;
    const originalHtmlPosition = html.style.position;
    const originalHtmlOverscroll = html.style.overscrollBehavior;
    const originalBodyOverflow = body.style.overflow;
    const originalBodyPosition = body.style.position;
    const originalBodyWidth = body.style.width;
    const originalBodyHeight = body.style.height;
    const originalBodyTop = body.style.top;
    const originalBodyOverscroll = body.style.overscrollBehavior;
    const originalRootHeight = root?.style.height || '';
    const originalRootOverflow = root?.style.overflow || '';
    const originalLockContainerHeight = lockContainer?.style.height || '';
    const originalLockContainerOverflow = lockContainer?.style.overflow || '';

    const initialHeight = getInitialViewportHeight();
    // On iOS, setting body.position=fixed causes iOS to shift the visual viewport
    // (offsetTop > 0) when a textarea is focused — this pushes position:fixed
    // elements above the visible area. The touchmove handler below is sufficient
    // to block background scroll on iOS without needing a fixed body.
    const iosDevice = isIOS();

    const preventScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest('[class*="messagesContainer"]') &&
        !target.closest('[class*="mobileNav"]') &&
        !target.closest('[class*="swipeTouchArea"]')
      ) {
        e.preventDefault();
      }
    };

    html.style.overflow = 'hidden';
    html.style.position = 'relative';
    html.style.overscrollBehavior = 'none';

    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    if (!iosDevice) {
      body.style.position = 'fixed';
      body.style.top = '0';
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }
    body.style.height = `${initialHeight}px`;

    if (root) {
      root.style.height = `${initialHeight}px`;
      root.style.overflow = 'hidden';
    }

    if (lockContainer) {
      lockContainer.style.height = `${initialHeight}px`;
      lockContainer.style.overflow = 'hidden';
    }

    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      html.style.overflow = originalHtmlOverflow;
      html.style.height = originalHtmlHeight;
      html.style.position = originalHtmlPosition;
      html.style.overscrollBehavior = originalHtmlOverscroll;
      html.style.width = '';
      html.style.top = '';

      body.style.overflow = originalBodyOverflow;
      body.style.height = originalBodyHeight;
      body.style.overscrollBehavior = originalBodyOverscroll;
      if (!iosDevice) {
        body.style.position = originalBodyPosition;
        body.style.width = originalBodyWidth;
        body.style.top = originalBodyTop;
      }

      if (root) {
        root.style.height = originalRootHeight;
        root.style.overflow = originalRootOverflow;
      }

      if (lockContainer) {
        lockContainer.style.height = originalLockContainerHeight;
        lockContainer.style.overflow = originalLockContainerOverflow;
      }

      document.removeEventListener('touchmove', preventScroll);

      window.scrollTo(0, scrollY);
    };
  }, [enabled, containerRef]);
};
